import * as React from "react";
import { splitEscaped } from "./split";

// Matched-pairs view of one run per configuration: one row per instance, one
// column per configuration, ✓ passed / · failed. Cells that differ from the
// baseline are marked ▲ (variant passed, baseline did not) or ▼ (baseline
// passed, variant did not) -- the discordant pairs McNemar's test uses. The
// footer counts them per variant, so "no variant beat the baseline" is visible
// as a column with no ▲.
//
// Props match the MDX registry entry:
// `labels`   comma-separated instance names (write "\," for a literal comma)
// `base`     baseline outcomes, one char per instance in label order
//            ("1" passed, anything else failed), e.g. "1101"
// `variants` comma-separated variant names
// `outcomes` semicolon-separated outcome strings, one per variant, same format
//            as `base` (write "\;" for a literal semicolon)
// `p`        optional adjusted p-value per variant, shown in the footer
//
// Strings shorter than the instance list treat the missing instances as "no
// data" for that column (shown as "?", left out of its counts). Rows with the
// most discordant cells come first; ties keep their authored order.

export interface PairedGridProps {
  labels: string;
  base: string;
  variants: string;
  outcomes: string;
  p?: number[];
}

const MAX_VARIANTS = 6;

// "org__repo-123" -> the visible short name; the org prefix stays in the
// accessible name so nothing is lost for screen readers.
function splitName(name: string): { prefix: string; main: string } {
  const at = name.indexOf("__");
  return at > 0 && at + 2 < name.length ? { prefix: name.slice(0, at + 2), main: name.slice(at + 2) } : { prefix: "", main: name };
}
const fmtP = (p: number) => (p < 0.001 ? "<0.001" : Number(p.toFixed(3)).toString());

type Cell = "pass" | "fail" | "none";
const cellOf = (s: string, i: number): Cell => (i >= s.length ? "none" : s[i] === "1" ? "pass" : "fail");

export function PairedGrid({ labels, base, variants, outcomes, p }: PairedGridProps) {
  const names = (labels ?? "") === "" ? [] : splitEscaped(labels ?? "", ",").map((s) => s.trim());
  const vNames = (variants ?? "") === "" ? [] : splitEscaped(variants ?? "", ",").map((s) => s.trim());
  const oList = (outcomes ?? "") === "" ? [] : splitEscaped(outcomes ?? "", ";").map((s) => s.trim());
  const nVar = Math.min(Math.max(vNames.length, oList.length), MAX_VARIANTS);
  const baseStr = (base ?? "").trim();
  const nRows = Math.max(names.length, baseStr.length);

  const cols = Array.from({ length: nVar }, (_, v) => ({
    name: vNames[v] || "(unnamed)",
    out: oList[v] ?? "",
  }));

  // Discordance of cell (row i, variant v): up = variant passed & baseline failed.
  const mark = (i: number, v: number): "up" | "down" | null => {
    const b = cellOf(baseStr, i);
    const c = cellOf(cols[v]!.out, i);
    if (b === "none" || c === "none" || b === c) return null;
    return c === "pass" ? "up" : "down";
  };

  const rows = Array.from({ length: nRows }, (_, i) => ({
    i,
    name: i < names.length ? names[i] || "—" : "(unnamed)",
    disc: cols.reduce((a, _c, v) => a + (mark(i, v) ? 1 : 0), 0),
  })).sort((a, b) => b.disc - a.disc);

  const tally = cols.map((_c, v) => {
    let up = 0;
    let down = 0;
    for (let i = 0; i < nRows; i++) {
      if (cellOf(baseStr, i) === "none" || cellOf(cols[v]!.out, i) === "none") continue;
      const m = mark(i, v);
      if (m === "up") up++;
      else if (m === "down") down++;
    }
    return { up, down };
  });

  const glyph = (c: Cell) => (c === "pass" ? "✓" : c === "fail" ? "·" : "?");
  const word = (c: Cell) => (c === "pass" ? "passed" : c === "fail" ? "failed" : "no data");

  return (
    <div className="gc-widget pr-widget">
      <div className="gc-widget-label" aria-hidden="true">
        {"// "}matched pairs vs baseline — {nRows} instances, {nVar} {nVar === 1 ? "variant" : "variants"}
      </div>
      <div className="pr-scroll">
        <table className="pr-table">
          <caption className="viz-sr">
            One run per configuration on {nRows} instances; rows that differ from the baseline come first.
            {cols.map((c, v) => ` ${c.name}: baseline only passed ${tally[v]!.down}, variant only passed ${tally[v]!.up}.`)}
          </caption>
          <thead>
            <tr>
              <th scope="col">instance</th>
              <th scope="col">baseline</th>
              {cols.map((c, v) => (
                <th scope="col" key={v}>
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, n) => {
              const b = cellOf(baseStr, r.i);
              return (
                <tr key={r.i} className={"pr-row" + (n > 0 && rows[n - 1]!.disc > 0 && r.disc === 0 ? " grp-start" : "")}>
                  <th scope="row">
                    {splitName(r.name).prefix && <span className="viz-sr">{splitName(r.name).prefix}</span>}
                    {splitName(r.name).main}
                  </th>
                  <td>
                    <span className="viz-sr">{word(b)}</span>
                    <span className={"pr-cell " + b} aria-hidden="true">
                      {glyph(b)}
                    </span>
                  </td>
                  {cols.map((c, v) => {
                    const cell = cellOf(c.out, r.i);
                    const m = mark(r.i, v);
                    return (
                      <td key={v}>
                        <span className="viz-sr">
                          {word(cell)}
                          {m === "up" ? ", passed where the baseline failed" : m === "down" ? ", failed where the baseline passed" : ""}
                        </span>
                        <span className={"pr-cell " + cell + (m ? " " + m : "")} aria-hidden="true">
                          {glyph(cell)}
                          {m && <sup>{m === "up" ? "▲" : "▼"}</sup>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">discordant ▼ / ▲</th>
              <td />
              {cols.map((_c, v) => (
                <td key={v} className="pr-tally">
                  {tally[v]!.down} / {tally[v]!.up}
                  {p && Number.isFinite(p[v]) && <span className="pr-p"> p={fmtP(p[v]!)}</span>}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="gc-legend" aria-hidden="true">
        <span>
          <span className="pr-cell pass">✓</span>passed
        </span>
        <span>
          <span className="pr-cell fail">·</span>failed
        </span>
        <span>▲ variant passed, baseline failed</span>
        <span>▼ baseline passed, variant failed</span>
      </div>
    </div>
  );
}

export default PairedGrid;
